import CancelButton from 'components/buttons/CancelButton';
import PermissionButton from 'components/buttons/PermissionButton';
import SaveButton from 'components/buttons/SaveButton';
import Loader from 'components/Loader';
import { SIMPLE_TABLE_CELL_CLASS, SIMPLE_TABLE_HEAD_CLASS, SIMPLE_TABLE_ROW_CLASS } from 'components/tables/BasicTable';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import React, { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useGetCheckExpiryConfigurationsQuery,
    useUpdateCheckExpiryConfigurationMutation,
} from 'store/api/compliance-check-expiry';
import { ComplianceCheckExpiryConfigDto, ComplianceCheckExpiryConfigUpdateDto } from 'types/compliance';
import { errorAlert } from 'utils/alert';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { CONFIGURATION_CARD_CLASS } from './configurationCard';

/** The table's cells at the compact density: 8px of padding, the row's ends still 16px in. */
const HEAD_CELL_CLASS = cn(SIMPLE_TABLE_HEAD_CLASS, 'p-2 font-semibold');
const NUMBER_HEAD_CELL_CLASS = cn(HEAD_CELL_CLASS, 'w-[140px] whitespace-nowrap');
const BODY_CELL_CLASS = cn(SIMPLE_TABLE_CELL_CLASS, 'p-2');

type EditableFields = Omit<
    ComplianceCheckExpiryConfigUpdateDto,
    'highRiskExpiryMonths' | 'stopOnboardingExpiryMonths'
> & {
    highRiskExpiryMonths: number | '';
};

type EditedRules = Record<number, Partial<EditableFields>>;

const formatPeriod = (months: number | null, unit: 'months' | 'days'): string => {
    if (months === null) return '-';
    return `${months} ${t(`configuration.${unit}`)}`;
};

const ComplianceCheckExpiryConfig = () => {
    const { data: configs, isLoading } = useGetCheckExpiryConfigurationsQuery();
    const [updateConfig] = useUpdateCheckExpiryConfigurationMutation();

    const [isEditMode, setIsEditMode] = useState(false);
    const [editedRules, setEditedRules] = useState<EditedRules>({});
    const [isSaving, setIsSaving] = useState(false);

    const handleToggleEnabled = useCallback((configId: number, currentValue: boolean) => {
        setEditedRules((prev) => ({
            ...prev,
            [configId]: { ...prev[configId], isAutoExpiryEnabled: !currentValue },
        }));
    }, []);

    const handleNumberChange = useCallback((configId: number, field: keyof EditableFields, value: string) => {
        setEditedRules((prev) => ({
            ...prev,
            [configId]: {
                ...prev[configId],
                [field]: value === '' ? '' : Number(value),
            },
        }));
    }, []);

    const handleCancel = useCallback(() => {
        setEditedRules({});
        setIsEditMode(false);
    }, []);

    const handleSave = useCallback(async () => {
        if (!configs) return;

        const changedEntries = Object.entries(editedRules).filter(([, changes]) => Object.keys(changes).length > 0);
        if (changedEntries.length === 0) {
            setIsEditMode(false);
            return;
        }

        setIsSaving(true);
        try {
            await Promise.all(
                changedEntries.map(([idStr, changes]) => {
                    const id = Number(idStr);
                    const original = configs.find((c) => c.id === id);
                    if (!original) return Promise.resolve();

                    const highRisk = changes.highRiskExpiryMonths ?? original.highRiskExpiryMonths;

                    const dto: { id: number } & ComplianceCheckExpiryConfigUpdateDto = {
                        id,
                        isAutoExpiryEnabled: changes.isAutoExpiryEnabled ?? original.isAutoExpiryEnabled,
                        expiryPeriodMonths:
                            typeof changes.expiryPeriodMonths === 'number'
                                ? changes.expiryPeriodMonths
                                : original.expiryPeriodMonths,
                        warningPeriodDays:
                            typeof changes.warningPeriodDays === 'number'
                                ? changes.warningPeriodDays
                                : original.warningPeriodDays,
                        highRiskExpiryMonths: highRisk === '' ? null : (highRisk ?? null),
                        stopOnboardingExpiryMonths: original.stopOnboardingExpiryMonths,
                    };
                    return updateConfig(dto).unwrap();
                })
            );
            setEditedRules({});
            setIsEditMode(false);
        } catch (e) {
            console.error('Update error:', e);
            errorAlert(t('forms.error-submitting-the-form'));
        } finally {
            setIsSaving(false);
        }
    }, [configs, editedRules, updateConfig]);

    if (isLoading) {
        return <Loader />;
    }

    return (
        <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
                <h3 className="text-h3 text-balance">
                    <FormattedMessage id="configuration.compliance-check-expiry" />
                </h3>
                {isEditMode ? (
                    <div className="flex gap-2">
                        <CancelButton onClick={handleCancel} disabled={isSaving} />
                        <SaveButton
                            onClick={() => {
                                void handleSave();
                            }}
                            disabled={isSaving}
                        />
                    </div>
                ) : (
                    <PermissionButton
                        permissions={['UPDATE.BACK_OFFICE_SETTINGS']}
                        variant="outlined"
                        size="small"
                        onClick={() => setIsEditMode(true)}
                    >
                        <FormattedMessage id="edit" />
                    </PermissionButton>
                )}
            </div>
            <div className={CONFIGURATION_CARD_CLASS}>
                <div className="relative w-full overflow-auto">
                    <table className="w-full text-left text-sm">
                        <TableHeader className="[&_tr]:border-b-0">
                            <TableRow className={SIMPLE_TABLE_ROW_CLASS}>
                                <TableHead className={cn(HEAD_CELL_CLASS, 'w-[1%]')} />
                                <TableHead className={HEAD_CELL_CLASS}>
                                    <FormattedMessage id="configuration.check-type" />
                                </TableHead>
                                <TableHead className={NUMBER_HEAD_CELL_CLASS}>
                                    <FormattedMessage id="configuration.expiry-period" />
                                </TableHead>
                                <TableHead className={NUMBER_HEAD_CELL_CLASS}>
                                    <FormattedMessage id="configuration.warning-period" />
                                </TableHead>
                                <TableHead className={NUMBER_HEAD_CELL_CLASS}>
                                    <FormattedMessage id="configuration.high-risk-override" />
                                </TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {configs?.map((config) => (
                                <ComplianceCheckExpiryRow
                                    key={config.id}
                                    config={config}
                                    isEditMode={isEditMode}
                                    editedFields={editedRules[config.id]}
                                    onToggleEnabled={handleToggleEnabled}
                                    onNumberChange={handleNumberChange}
                                />
                            ))}
                        </TableBody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default ComplianceCheckExpiryConfig;

// ─── Row ────────────────────────────────────────────────────────────────────

interface RowProps {
    config: ComplianceCheckExpiryConfigDto;
    isEditMode: boolean;
    editedFields?: Partial<EditableFields>;
    onToggleEnabled: (configId: number, currentValue: boolean) => void;
    onNumberChange: (configId: number, field: keyof EditableFields, value: string) => void;
}

const ComplianceCheckExpiryRow: React.FC<RowProps> = ({
    config,
    isEditMode,
    editedFields,
    onToggleEnabled,
    onNumberChange,
}) => {
    const getEnabled = () => editedFields?.isAutoExpiryEnabled ?? config.isAutoExpiryEnabled;

    const checkTypeLabel = t(`compliance.check-type.${config.checkTypeCode}`);
    type NumericField = Exclude<keyof EditableFields, 'isAutoExpiryEnabled'>;
    const getNumber = (field: NumericField, original: number | null): number | '' | null =>
        (editedFields?.[field] as number | '' | undefined) ?? original;

    return (
        <TableRow className={cn(SIMPLE_TABLE_ROW_CLASS, !isEditMode && !getEnabled() && 'opacity-50')}>
            <TableCell className={BODY_CELL_CLASS}>
                <Checkbox
                    className="mx-auto"
                    checked={getEnabled()}
                    disabled={!isEditMode}
                    aria-label={checkTypeLabel}
                    onCheckedChange={() => onToggleEnabled(config.id, getEnabled())}
                />
            </TableCell>
            <TableCell className={BODY_CELL_CLASS}>
                <p className="text-body1 font-semibold">{checkTypeLabel}</p>
            </TableCell>
            <NumericCell
                isEditMode={isEditMode}
                value={getNumber('expiryPeriodMonths', config.expiryPeriodMonths)}
                unit="months"
                inputProps={{ min: 1, max: 120 }}
                ariaLabel={`${checkTypeLabel}: ${t('configuration.expiry-period')}`}
                onChange={(v) => onNumberChange(config.id, 'expiryPeriodMonths', v)}
            />
            <NumericCell
                isEditMode={isEditMode}
                value={getNumber('warningPeriodDays', config.warningPeriodDays)}
                unit="days"
                inputProps={{ min: 1, max: 365 }}
                ariaLabel={`${checkTypeLabel}: ${t('configuration.warning-period')}`}
                onChange={(v) => onNumberChange(config.id, 'warningPeriodDays', v)}
            />
            <NumericCell
                isEditMode={isEditMode}
                value={getNumber('highRiskExpiryMonths', config.highRiskExpiryMonths)}
                unit="months"
                inputProps={{ min: 0, max: 120 }}
                nullable
                ariaLabel={`${checkTypeLabel}: ${t('configuration.high-risk-override')}`}
                onChange={(v) => onNumberChange(config.id, 'highRiskExpiryMonths', v)}
            />
        </TableRow>
    );
};

// ─── Numeric Cell ───────────────────────────────────────────────────────────

interface NumericCellProps {
    isEditMode: boolean;
    value: number | '' | null | undefined;
    unit: 'months' | 'days';
    inputProps: { min: number; max: number };
    nullable?: boolean;
    ariaLabel: string;
    onChange: (value: string) => void;
}

const NumericCell: React.FC<NumericCellProps> = ({ isEditMode, value, unit, inputProps, ariaLabel, onChange }) => {
    if (!isEditMode) {
        return (
            <TableCell className={BODY_CELL_CLASS}>
                {formatPeriod(value === '' || value == null ? null : (value as number), unit)}
            </TableCell>
        );
    }

    return (
        <TableCell className={BODY_CELL_CLASS}>
            <Input
                type="number"
                value={value ?? ''}
                {...inputProps}
                aria-label={ariaLabel}
                className="bg-paper h-7 w-20 px-1 text-center"
                onChange={(e) => onChange(e.target.value)}
            />
        </TableCell>
    );
};
